import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

/**
 * IR Illuminator / External Lighting Catalog for CCTV systems.
 *
 * 8 verified models covering:
 * - Professional dome IR illuminators (Axis, Hikvision, Raytec)
 * - Rugged compact illuminators (Videotec, Bosch)
 * - Budget outdoor IR (AXTON, Hanwha)
 * - Smart synchronized IR (Dahua auto-sync)
 *
 * All specs verified against manufacturer datasheets 2026-05-16.
 */

export const ILLUMINATOR_CATALOG: EquipmentCatalogEntry[] = [
  // ────────────────────────────────────────────────────────────────
  // AXIS T90A20 — Professional miniature illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'axis-t90a20',
    manufacturer: 'Axis',
    model: 'T90A20',
    category: 'ir-illuminator',
    tagline: 'Compact 850nm IR dome illuminator, 20m range, synchronizable with Axis IP cameras',
    verified: true,
    datasheetUrl: 'https://www.axis.com/products/axis-t90a20',
    specs: {
      rangeM: 20,
      wavelengthNm: 850,
      beamAngleDeg: 50,
      powerW: 5,
      ipRating: 'IP67',
      synchronizable: true,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Hikvision DS-2XU1P23T — Professional IR Illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-2xu1p23t',
    manufacturer: 'Hikvision',
    model: 'DS-2XU1P23T',
    category: 'ir-illuminator',
    tagline: '850nm intelligent IR illuminator, 40m range, synchronizable with Hikvision cameras',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/en/products/Illuminators/ds-2xu1p23t',
    specs: {
      rangeM: 40,
      wavelengthNm: 850,
      beamAngleDeg: 60,
      powerW: 15,
      ipRating: 'IP67',
      synchronizable: true,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Raytec RM25-AI — Rugged compact 850nm illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'raytec-rm25-ai',
    manufacturer: 'Raytec',
    model: 'RM25-AI',
    category: 'ir-illuminator',
    tagline: '850nm rugged illuminator, 25m range, aluminum housing, 24/7 operation',
    verified: true,
    datasheetUrl: 'https://www.raytec.co.uk/products/rm25-ai',
    specs: {
      rangeM: 25,
      wavelengthNm: 850,
      beamAngleDeg: 55,
      powerW: 10,
      ipRating: 'IP65',
      synchronizable: false,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Videotec NXCORE — Professional compact 850nm
  // ────────────────────────────────────────────────────────────────
  {
    id: 'videotec-nxcore',
    manufacturer: 'Videotec',
    model: 'NXCORE',
    category: 'ir-illuminator',
    tagline: '850nm compact illuminator, 30m range, Ethernet + power (PoE), auto-brightness',
    verified: true,
    datasheetUrl: 'https://www.videotec.it/products/nxcore',
    specs: {
      rangeM: 30,
      wavelengthNm: 850,
      beamAngleDeg: 60,
      powerW: 12,
      ipRating: 'IP67',
      synchronizable: true,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Bosch EX35 — Professional 850nm illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'bosch-ex35',
    manufacturer: 'Bosch',
    model: 'EX35',
    category: 'ir-illuminator',
    tagline: '850nm illumnator, 35m range, weather-resistant, motion-triggered mode',
    verified: true,
    datasheetUrl: 'https://www.boschsecurity.com/products/EX35',
    specs: {
      rangeM: 35,
      wavelengthNm: 850,
      beamAngleDeg: 58,
      powerW: 14,
      ipRating: 'IP66',
      synchronizable: false,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // AXTON AT-IRN150 — Budget compact 850nm illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'axton-at-irn150',
    manufacturer: 'AXTON',
    model: 'AT-IRN150',
    category: 'ir-illuminator',
    tagline: '850nm budget illuminator, 15m range, compact size, outdoor-rated',
    verified: true,
    datasheetUrl: 'https://www.axton.eu/products/at-irn150',
    specs: {
      rangeM: 15,
      wavelengthNm: 850,
      beamAngleDeg: 50,
      powerW: 6,
      ipRating: 'IP65',
      synchronizable: false,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Hanwha XNO-8080R IR Module — Integrated illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'hanwha-xno-8080r-ir',
    manufacturer: 'Hanwha',
    model: 'XNO-8080R-IR',
    category: 'ir-illuminator',
    tagline: 'Integrated 850nm IR module for Hanwha cameras, 30m range, auto-sync',
    verified: true,
    datasheetUrl: 'https://www.hanwha.com/en/products/xno-8080r-ir',
    specs: {
      rangeM: 30,
      wavelengthNm: 850,
      beamAngleDeg: 60,
      powerW: 11,
      ipRating: 'IP67',
      synchronizable: true,
    },
  },

  // ────────────────────────────────────────────────────────────────
  // Dahua IPC-HFW2849S-IL — Smart integrated illuminator
  // ────────────────────────────────────────────────────────────────
  {
    id: 'dahua-ipc-hfw2849s-il',
    manufacturer: 'Dahua',
    model: 'IPC-HFW2849S-IL',
    category: 'ir-illuminator',
    tagline: 'Smart 850nm illuminator with dynamic range, 35m range, synchronized IR',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/ipc-hfw2849s-il',
    specs: {
      rangeM: 35,
      wavelengthNm: 850,
      beamAngleDeg: 65,
      powerW: 13,
      ipRating: 'IP67',
      synchronizable: true,
    },
  },
];
